/* eslint-disable react/prop-types */
import React from 'react';
import { SKILL_SCORE_GROUPS } from '../calculator';
import { abbreviateName, fmtTimeCompact, isNonMovableStatus, statusInfo } from '../utils/format';
import KmDelta from './KmDelta';

const isMealRow = job => String(job?.neighbourhood || '')
  .toLocaleLowerCase('tr-TR')
  .trim() === 'yemek';

function BestMoveCell({ bestMove }) {
  if (!bestMove) return <td className="best-move-cell"><span className="empty-inline">—</span></td>;
  const scoreGroup = SKILL_SCORE_GROUPS.find(group => group.score === Number(bestMove.skillScore));
  const scoreClass = scoreGroup ? `skill-score-${scoreGroup.score}` : 'skill-score-other';
  const fitInfo = bestMove.fit === 'EtaViolation'
    ? { label: 'Randevu İhlali', className: 'fit-eta' }
    : bestMove.fit === 'Overtime'
      ? { label: 'Mesai Aşımı', className: 'fit-overtime' }
      : null;
  const shortName = abbreviateName(bestMove.bestTechName);
  const sign = bestMove.combinedKmDelta > 0 ? '+' : '';
  const titleParts = [
    bestMove.bestTechName,
    scoreGroup?.label || 'Score Bulunamadı',
    `${sign}${bestMove.combinedKmDelta.toFixed(1)} km`,
  ];
  if (fitInfo) titleParts.push(fitInfo.label);
  return (
    <td className="best-move-cell" title={titleParts.join(' · ')}>
      <span className={`best-tech-chip ${scoreClass}`}>{shortName}</span>
      <KmDelta value={bestMove.combinedKmDelta} />
      {fitInfo && (
        <span className={`fit-pill fit-pill-sm best-move-warning ${fitInfo.className}`}>
          {fitInfo.label}
        </span>
      )}
    </td>
  );
}

function StatusCell({ statusDescription }) {
  const { code, label, cls } = statusInfo(statusDescription);
  return (
    <td className="status-cell" title={label}>
      <span className={`status-text ${cls}`}>{code}</span>
    </td>
  );
}

function JobRow({
  job,
  displaySortOrder,
  selectable,
  showBestMove,
  showStatus,
  bestMove,
  highlighted,
  highlightEffect,
  selected,
  onSelectJob,
}) {
  const isNonMovable = isNonMovableStatus(job.statusDescription);
  const rowSelectable = selectable && !isNonMovable;
  const classes = [
    rowSelectable ? 'job-row' : '',
    isNonMovable ? 'row-passive' : '',
    rowSelectable && selected ? 'selected' : '',
    job.isEtaViolation ? 'eta-violation' : '',
    highlighted ? `moved-job-row moved-job-${highlightEffect || 'neutral'}` : '',
  ].filter(Boolean).join(' ');

  return (
    <tr
      className={classes || undefined}
      onClick={rowSelectable ? () => onSelectJob(job.ofscNr) : undefined}
    >
      <td>{displaySortOrder}</td>
      <td className="cell-customer" title={job.customer || ''}>
        {job.customer || '-'}
        {highlighted && (
          <span className="moved-badge">
            {highlightEffect === 'positive'
              ? 'TAŞINAN İŞ · KM KISALDI'
              : highlightEffect === 'negative'
                ? 'TAŞINAN İŞ · KM UZADI'
                : 'TAŞINAN İŞ · KM DEĞİŞMEDİ'}
          </span>
        )}
      </td>
      <td className="cell-dealer" title={job.dealer || ''}>{job.dealer || '-'}</td>
      {showBestMove && <BestMoveCell bestMove={bestMove} />}
      <td className="cell-desc" title={job.appReason || job.products || '-'}>
        {job.appReason || job.products || '-'}
      </td>
      <td className="cell-neighbourhood" title={job.neighbourhood || ''}>
        {job.neighbourhood || '-'}
      </td>
      <td>{fmtTimeCompact(job.appointmentTime)}-{fmtTimeCompact(job.appointmentTimeEnd)}</td>
      <td className="arrival-cell">{job.arrivalatCustomer || '-'}</td>
      <td title={job.workDurationType || 'Süre'}>
        {job.displayWorkDuration ?? job.workDuration} dk
      </td>
      {showStatus && <StatusCell statusDescription={job.statusDescription} />}
    </tr>
  );
}

// jobs: RouteStopDto tabanlı iş listesi (backend'in TechnicianScheduleCalculator ile
// hesapladığı sıra/varış/durum bilgileriyle). selectable=true olan tek yer sol paneldeki
// teknisyen iş listesi — sağ paneldeki "taşımadan sonra" mini tabloları salt görüntüleme.
export default function JobTable({
  jobs,
  selectable = false,
  bestMoves,
  highlightOfscNr,
  highlightEffect = 'neutral',
  selectedOfscNr,
  onSelectJob,
  showStatus = true,
}) {
  const hasBestMove = !!bestMoves;
  const placeholderColSpan = (hasBestMove ? 9 : 8) - (showStatus ? 0 : 1);
  let precedingMealCount = 0;
  const displayJobs = jobs.map((job) => {
    const mealRow = isMealRow(job);
    const numericSortOrder = Number(job.sortOrder);
    const displaySortOrder = mealRow
      ? ''
      : (Number.isFinite(numericSortOrder) ? numericSortOrder - precedingMealCount : job.sortOrder);
    if (mealRow) precedingMealCount += 1;
    return { job, displaySortOrder };
  });
  return (
    <table>
      <thead>
        <tr>
          <th>Sıra</th>
          <th>Müşteri</th>
          <th>Bayi</th>
          {hasBestMove && <th>En İyi Taşıma</th>}
          <th>Başvuru Nedeni</th>
          <th>Mahalle</th>
          <th>Randevu</th>
          <th>Varış</th>
          <th>Süre</th>
          {showStatus && <th>Durum</th>}
        </tr>
      </thead>
      <tbody>
        {displayJobs.map(({ job, displaySortOrder }) => job.isPlaceholder ? (
          <tr className="placeholder-row" key={`ph-${job.sortOrder}`}>
            <td>{displaySortOrder}</td>
            <td colSpan={placeholderColSpan}>{job.neighbourhood || '-'} (mola/servis)</td>
          </tr>
        ) : (
          <JobRow
            key={job.ofscNr}
            job={job}
            displaySortOrder={displaySortOrder}
            selectable={selectable}
            showBestMove={hasBestMove}
            showStatus={showStatus}
            bestMove={hasBestMove ? bestMoves.get(job.ofscNr) : undefined}
            highlighted={!!highlightOfscNr && job.ofscNr === highlightOfscNr}
            highlightEffect={highlightEffect}
            selected={job.ofscNr === selectedOfscNr}
            onSelectJob={onSelectJob}
          />
        ))}
      </tbody>
    </table>
  );
}
